const TAIL_SIZE = 8192;
const CHUNK_SIZE = 65536;
const OVERLAP_SIZE = 64;

function readSliceAsText(file: File, start: number, end: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file slice"));
        reader.readAsBinaryString(file.slice(start, end));
    });
}

function extractRootPageCount(text: string): number {
    const pattern = /\/Type\s*\/Pages[^]*?\/Count\s+(\d+)/g;
    let maxCount = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        const count = parseInt(match[1], 10);
        if (count > maxCount) {
            maxCount = count;
        }
    }
    return maxCount;
}

async function countPageObjects(file: File): Promise<number> {
    const pattern = /\/Type\s*\/Page(?!\s*s)\b/g;
    let count = 0;
    let offset = 0;
    let carry = "";

    while (offset < file.size) {
        const end = Math.min(offset + CHUNK_SIZE, file.size);
        const chunk = await readSliceAsText(file, offset, end);
        const searchText = carry + chunk;

        pattern.lastIndex = 0;
        while (pattern.exec(searchText) !== null) {
            count++;
        }

        carry = searchText.length > OVERLAP_SIZE ? searchText.slice(-OVERLAP_SIZE) : searchText;
        offset = end;
    }

    return count;
}

/**
 * Extracts page count from a PDF file without loading the entire file into memory.
 * First checks the last 8KB for the root /Pages /Count entry.
 * Falls back to streaming through the file counting individual /Type /Page objects.
 * Throws on read errors so callers can handle them with user-facing messages.
 */
export async function getPdfPageCount(file: File): Promise<number> {
    const tailStart = Math.max(0, file.size - TAIL_SIZE);
    const tail = await readSliceAsText(file, tailStart, file.size);
    const rootCount = extractRootPageCount(tail);
    if (rootCount > 0) {
        return rootCount;
    }

    const streamCount = await countPageObjects(file);
    return streamCount > 0 ? streamCount : 1;
}
